import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Wallet, Upload, ArrowUpCircle, ArrowDownCircle, Clock, CheckCircle, XCircle, Loader2, Calculator } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientWalletPage = () => {
  const { getAuthHeaders } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [rechargeConfigs, setRechargeConfigs] = useState([]);
  const [showTopup, setShowTopup] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [topupForm, setTopupForm] = useState({ amount: '', payment_method_id: '', holder_name: '' });
  const [proofFile, setProofFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const h = { headers: getAuthHeaders() };
      const [w, t, pm, rc] = await Promise.all([
        axios.get(`${API}/client/wallet/summary`, h),
        axios.get(`${API}/client/wallet/transactions`, h),
        axios.get(`${API}/client/payment-methods`, h),
        axios.get(`${API}/admin/recharge-config/active`, h).catch(() => ({ data: [] })),
      ]);
      setWallet(w.data);
      setTransactions(t.data);
      setPaymentMethods(pm.data);
      setRechargeConfigs(rc.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleTopup = async (e) => {
    e.preventDefault();
    if (!proofFile) { toast.error('Debes subir un comprobante de pago'); return; }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('amount', topupForm.amount);
      formData.append('payment_method_id', topupForm.payment_method_id);
      formData.append('holder_name', topupForm.holder_name);
      formData.append('proof_image', proofFile);
      if (selectedMethod) {
        formData.append('recharge_method', selectedMethod.method_name);
        formData.append('recharge_currency', selectedMethod.currency_code);
      }
      await axios.post(`${API}/client/wallet/topup`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Solicitud de recarga enviada. Pendiente de aprobacion.');
      setShowTopup(false);
      setTopupForm({ amount: '', payment_method_id: '', holder_name: '' });
      setProofFile(null);
      setSelectedMethod(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al enviar solicitud');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;

  const statusIcon = (s) => {
    if (s === 'approved') return <CheckCircle className="w-4 h-4 text-green-500" />;
    if (s === 'rejected') return <XCircle className="w-4 h-4 text-red-500" />;
    return <Clock className="w-4 h-4 text-yellow-500" />;
  };

  // Calculate what the user needs to pay
  const usdAmount = parseFloat(topupForm.amount) || 0;
  const costInCurrency = selectedMethod && usdAmount > 0
    ? usdAmount * selectedMethod.exchange_rate * (1 + (selectedMethod.commission_pct || 0) / 100)
    : 0;

  return (
    <div className="space-y-6" data-testid="client-wallet-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Mi Wallet</h1>
          <p className="text-slate-500">Gestiona tu saldo y transacciones</p>
        </div>
        <Button onClick={() => setShowTopup(!showTopup)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="topup-toggle-btn">
          <ArrowUpCircle className="w-4 h-4 mr-2" /> Recargar
        </Button>
      </div>

      {/* Balance Card */}
      <Card className="p-6 bg-gradient-to-br from-emerald-600 to-emerald-800 text-white border-0">
        <div className="flex items-center gap-3 mb-4">
          <Wallet className="w-8 h-8" />
          <span className="text-emerald-200 text-lg">Saldo disponible</span>
        </div>
        <p className="text-4xl font-bold" data-testid="wallet-balance">${(wallet?.balance || 0).toFixed(2)}</p>
        <div className="grid grid-cols-3 gap-4 mt-6 pt-4 border-t border-white/20 text-sm">
          <div><p className="text-emerald-200">Depositos</p><p className="font-semibold">${(wallet?.total_deposits || 0).toFixed(2)}</p></div>
          <div><p className="text-emerald-200">Servicios</p><p className="font-semibold">${(wallet?.total_services || 0).toFixed(2)}</p></div>
          <div><p className="text-emerald-200">Reembolsos</p><p className="font-semibold">${(wallet?.total_refunds || 0).toFixed(2)}</p></div>
        </div>
      </Card>

      {/* Topup Form */}
      {showTopup && (
        <Card className="p-6 border-emerald-200 bg-emerald-50" data-testid="topup-form">
          <h3 className="font-semibold text-slate-900 mb-4">Solicitar Recarga</h3>

          {/* Recharge Methods */}
          {rechargeConfigs.length > 0 && (
            <div className="mb-5">
              <Label className="mb-2 block">Metodo de recarga</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {rechargeConfigs.map((rc) => (
                  <div
                    key={rc.id}
                    className={`p-3 rounded-xl border-2 cursor-pointer transition-all text-center ${selectedMethod?.id === rc.id ? 'border-emerald-500 bg-emerald-100 shadow' : 'border-slate-200 hover:border-emerald-300 bg-white'}`}
                    onClick={() => setSelectedMethod(rc)}
                    data-testid={`recharge-method-${rc.id}`}
                  >
                    <p className="font-semibold text-sm">{rc.method_name}</p>
                    <p className="text-xs text-slate-500">{rc.currency_code}</p>
                    <p className="text-xs text-emerald-600 mt-1">1 USD = {rc.exchange_rate} {rc.currency_code}</p>
                    {rc.commission_pct > 0 && <p className="text-[10px] text-slate-400">Com: {rc.commission_pct}%</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <form onSubmit={handleTopup} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Monto deseado (USD en Wallet)</Label>
                <Input type="number" step="0.01" min="1" value={topupForm.amount} onChange={(e) => setTopupForm({ ...topupForm, amount: e.target.value })} placeholder="100.00" required data-testid="topup-amount" />
              </div>
              <div className="space-y-2">
                <Label>Nombre del titular</Label>
                <Input value={topupForm.holder_name} onChange={(e) => setTopupForm({ ...topupForm, holder_name: e.target.value })} placeholder="Nombre en la transferencia" required data-testid="topup-holder" />
              </div>
            </div>

            {/* Cost Calculator */}
            {selectedMethod && usdAmount > 0 && (
              <div className="p-4 bg-white rounded-xl border-2 border-emerald-300" data-testid="recharge-calculator">
                <div className="flex items-center gap-2 mb-2">
                  <Calculator className="w-5 h-5 text-emerald-600" />
                  <span className="font-semibold text-sm text-slate-900">Calculadora de recarga</span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Recibiras en wallet:</p>
                    <p className="text-xl font-bold text-emerald-700">${usdAmount.toFixed(2)} USD</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Debes pagar:</p>
                    <p className="text-xl font-bold text-blue-700">{costInCurrency.toFixed(2)} {selectedMethod.currency_code}</p>
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  Tasa: 1 USD = {selectedMethod.exchange_rate} {selectedMethod.currency_code} | Comision: {selectedMethod.commission_pct}%
                </p>
                {selectedMethod.instructions && (
                  <p className="text-xs text-blue-600 mt-2 p-2 bg-blue-50 rounded">{selectedMethod.instructions}</p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label>Metodo de pago (cuenta destino)</Label>
              <select
                value={topupForm.payment_method_id}
                onChange={(e) => setTopupForm({ ...topupForm, payment_method_id: e.target.value })}
                className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
                required
                data-testid="topup-method-select"
              >
                <option value="">Selecciona una cuenta</option>
                {paymentMethods.map((pm) => (
                  <option key={pm.id} value={pm.id}>{pm.bank_name} - {pm.method_type} ({pm.country})</option>
                ))}
              </select>
            </div>

            {/* Selected payment method details */}
            {topupForm.payment_method_id && (() => {
              const pm = paymentMethods.find(m => m.id === topupForm.payment_method_id);
              if (!pm) return null;
              return (
                <div className="p-3 bg-white rounded-lg border text-sm">
                  <p className="font-medium text-slate-900">{pm.bank_name}</p>
                  <p className="text-slate-600">Cuenta: {pm.account_number}</p>
                  <p className="text-slate-600">Titular: {pm.account_holder}</p>
                  {pm.notes && <p className="text-slate-500 mt-1">{pm.notes}</p>}
                </div>
              );
            })()}

            <div className="space-y-2">
              <Label>Comprobante de pago</Label>
              <div
                className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center cursor-pointer hover:border-emerald-500 transition-colors"
                onClick={() => fileRef.current?.click()}
                data-testid="topup-proof-upload"
              >
                <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                <p className="text-sm text-slate-600">{proofFile ? proofFile.name : 'Haz clic para subir imagen'}</p>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => setProofFile(e.target.files[0])} />
              </div>
            </div>

            <div className="flex gap-3">
              <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="topup-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enviar solicitud'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowTopup(false)}>Cancelar</Button>
            </div>
          </form>
        </Card>
      )}

      {/* Transactions */}
      <Card className="p-6 border-0 shadow-sm">
        <h2 className="font-semibold text-slate-900 mb-4">Historial de transacciones</h2>
        {transactions.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <Wallet className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p>Sin transacciones</p>
          </div>
        ) : (
          <div className="space-y-2">
            {transactions.map((t) => (
              <div key={t.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`wallet-txn-${t.id}`}>
                <div className="flex items-center gap-3">
                  {t.amount > 0 ? <ArrowUpCircle className="w-5 h-5 text-green-500" /> : <ArrowDownCircle className="w-5 h-5 text-red-500" />}
                  <div>
                    <p className="text-sm font-medium text-slate-900">{t.description || t.transaction_type}</p>
                    <p className="text-xs text-slate-500">{new Date(t.created_at).toLocaleString('es-ES')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`font-semibold text-sm ${t.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {t.amount > 0 ? '+' : ''}${Math.abs(t.amount).toFixed(2)}
                  </span>
                  {statusIcon(t.status)}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default ClientWalletPage;
